import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useParams } from "react-router-dom"
import { eskaliereKvpAufgabe, fetchKvpAufgaben, updateKvpAufgabe } from "@/lib/api"
import { BerichtsThread } from "@/components/BerichtsThread"
import { EmployeePicker } from "@/components/EmployeePicker"
import { Button } from "@/components/ui"
import { useToast, fehlerNachricht } from "@/lib/toast"

/**
 * Aufgaben-Detail (AP-4.8): Zuständigkeiten, Eskalation zu Serviceticket
 * (05-F8) und der zentrale Berichts-Thread (Berichte & Kommunikation,
 * rmnc-nachrichten) — ersetzt den früheren KvpEintrag-Kommentar-Thread und
 * das E-Mail-Benachrichtigungs-Formular (E6-Ablösung).
 *
 * Route-Wrapper (Deep-Link-Fallback, `/kvp/aufgaben/:id`) — liest die ID aus der
 * URL und delegiert an `KvpAufgabeDetailContent`. Primärer Aufrufweg ist das
 * Sheet aus `KvpBoardPage` (UX_KONZEPT.md §7).
 */
export function KvpAufgabeDetailPage() {
  const { id } = useParams<{ id: string }>()
  const aufgabeId = Number(id)
  return (
    <div>
      <Link
        to="/kvp"
        className="mb-2 inline-block rounded-md text-sm text-slate-500 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400/60 focus-visible:ring-offset-1"
      >
        ← zurück zum Board
      </Link>
      <KvpAufgabeDetailContent aufgabeId={aufgabeId} />
    </div>
  )
}

/** Inhalt der KVP-Aufgaben-Detailansicht — wird von der Route (Deep-Link-Fallback)
 * und vom Sheet (`KvpBoardPage`) genutzt. */
export function KvpAufgabeDetailContent({ aufgabeId }: { aufgabeId: number }) {
  const queryClient = useQueryClient()
  const toast = useToast()

  const { data: aufgaben } = useQuery({
    queryKey: ["kvp-aufgaben-alle"],
    queryFn: () => fetchKvpAufgaben(),
  })
  const aufgabe = aufgaben?.find((a) => a.id === aufgabeId)

  const eskalieren = useMutation({
    mutationFn: () => eskaliereKvpAufgabe(aufgabeId),
    onError: (err) => toast.error(fehlerNachricht(err, "Eskalation zu Serviceticket ist fehlgeschlagen.")),
  })

  const zustaendigenAendern = useMutation({
    mutationFn: (zustaendigEmployeeId: string | null) =>
      updateKvpAufgabe(aufgabeId, { zustaendigEmployeeId: zustaendigEmployeeId ?? "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kvp-aufgaben-alle"] })
      toast.success("Zuständigkeit aktualisiert.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Zuständigkeit konnte nicht geändert werden.")),
  })

  const aufgabenstellerAendern = useMutation({
    mutationFn: (aufgabenstellerEmployeeId: string | null) =>
      updateKvpAufgabe(aufgabeId, { aufgabenstellerEmployeeId: aufgabenstellerEmployeeId ?? "" }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["kvp-aufgaben-alle"] })
      toast.success("Aufgabensteller aktualisiert.")
    },
    onError: (err) => toast.error(fehlerNachricht(err, "Aufgabensteller konnte nicht geändert werden.")),
  })

  if (!aufgabe) return <p className="text-sm text-slate-500">Lade…</p>

  return (
    <div className="space-y-6">
      <header>
        <p className="font-mono text-xs text-slate-400">{aufgabe.businessId}</p>
        <h1 className="text-2xl font-semibold text-slate-900">{aufgabe.bezeichnung}</h1>
      </header>

      <section className="grid grid-cols-1 gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2">
        <EmployeePicker
          value={aufgabe.zustaendigEmployeeId}
          onChange={(id) => zustaendigenAendern.mutate(id)}
          label="Zuständig"
        />
        <EmployeePicker
          value={aufgabe.aufgabenstellerEmployeeId}
          onChange={(id) => aufgabenstellerAendern.mutate(id)}
          label="Aufgabensteller"
        />
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4">
        <h2 className="mb-3 text-sm font-semibold text-slate-700">Serviceticket-Eskalation (05-F8)</h2>
        <Button
          variant="destructive"
          onClick={() => eskalieren.mutate()}
          disabled={eskalieren.isPending}
          className="h-8 px-3 text-sm"
        >
          Zu Serviceticket eskalieren
        </Button>
        {eskalieren.isSuccess && (
          <p className="mt-2 text-sm text-green-700">
            Ticket {eskalieren.data.ticketKey} erstellt.
          </p>
        )}
      </section>

      {/* Zentraler Berichts-Thread (rmnc-nachrichten): Kommentare,
          Zwischen-/Endberichte inkl. optionaler E-Mail an die Teilnehmer;
          erscheint bei allen Beteiligten auch in der Portal-Inbox. */}
      <BerichtsThread objektTyp="kvp_aufgabe" objektId={String(aufgabeId)} />
    </div>
  )
}
